const DEFAULT_EXCERPT_LENGTH = 160;
const activeConversationSurfaces = new Map();

const normalizeValue = (value) => String(value ?? '').trim();

const normalizeChannel = (value) => {
  const channel = normalizeValue(value).toLowerCase();
  if (['jarvis', 'jarvis_chat_reply'].includes(channel)) return 'jarvis';
  if (['sms', 'sms_inbound'].includes(channel)) return 'sms';
  if (['live_chat', 'website_live_chat'].includes(channel)) return 'live_chat';
  if (['team', 'team_chat', 'team_message'].includes(channel)) return 'team';
  return channel;
};

export const notificationMetadata = (notification) => {
  if (notification?.metadata && typeof notification.metadata === 'object') {
    return notification.metadata;
  }
  try {
    const parsed = JSON.parse(notification?.metadata || '{}');
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_error) {
    return {};
  }
};

const actionUrlParams = (notification) => {
  const actionUrl = normalizeValue(notification?.actionUrl);
  if (!actionUrl) return new URLSearchParams();
  try {
    return new URL(actionUrl, 'https://autoclever.local').searchParams;
  } catch (_error) {
    return new URLSearchParams();
  }
};

const firstValue = (...values) => values
  .map(normalizeValue)
  .find(Boolean) || '';

export const notificationExcerpt = (value, maxLength = DEFAULT_EXCERPT_LENGTH) => {
  const normalized = normalizeValue(value)
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/[`*_>#~]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const limit = Math.max(24, Number(maxLength) || DEFAULT_EXCERPT_LENGTH);
  if (normalized.length <= limit) return normalized;

  const candidate = normalized.slice(0, limit + 1);
  const wordBoundary = candidate.lastIndexOf(' ');
  const end = wordBoundary >= Math.floor(limit * 0.65) ? wordBoundary : limit;
  const words = candidate.slice(0, end).trim().split(' ');
  if (words.length > 1 && words[words.length - 1].length <= 2) words.pop();
  return `${words.join(' ').replace(/[.,;:!?-]+$/, '')}...`;
};

export const registerActiveConversationSurface = (
  surfaceId,
  channel,
  conversationId,
  visible = true
) => {
  const key = normalizeValue(surfaceId);
  if (!key) return () => {};

  const normalizedChannel = normalizeChannel(channel);
  const normalizedId = normalizeValue(conversationId);
  const registration = Symbol(key);
  if (visible && normalizedChannel && normalizedId) {
    activeConversationSurfaces.set(key, {
      channel: normalizedChannel,
      conversationId: normalizedId,
      registration
    });
  } else {
    activeConversationSurfaces.delete(key);
  }

  return () => {
    if (activeConversationSurfaces.get(key)?.registration === registration) {
      activeConversationSurfaces.delete(key);
    }
  };
};

export const notificationConversationIdentity = (notification) => {
  const metadata = notificationMetadata(notification);
  const params = actionUrlParams(notification);
  const entityType = normalizeValue(notification?.entityType).toLowerCase();
  let channel = normalizeChannel(notification?.type);

  if (!['jarvis', 'sms', 'live_chat', 'team'].includes(channel)) {
    if (entityType === 'jarvis_conversation') channel = 'jarvis';
    else if (entityType === 'live_chat_thread') channel = 'live_chat';
    else if (entityType === 'team_conversation') channel = 'team';
  }

  let conversationId = '';
  if (channel === 'jarvis') {
    conversationId = firstValue(
      metadata.conversationId,
      metadata.conversation_id,
      params.get('conversationId'),
      entityType === 'jarvis_conversation' ? notification?.entityId : ''
    );
  } else if (channel === 'sms') {
    conversationId = firstValue(
      metadata.customerId,
      metadata.customer_id,
      metadata.threadId,
      params.get('customerId')
    );
  } else if (channel === 'live_chat') {
    conversationId = firstValue(
      metadata.sessionId,
      metadata.session_id,
      params.get('sessionId')
    );
  } else if (channel === 'team') {
    conversationId = firstValue(
      metadata.conversationId,
      metadata.conversation_id,
      params.get('conversationId'),
      entityType === 'team_conversation' ? notification?.entityId : ''
    );
  }

  return channel && conversationId ? { channel, conversationId } : null;
};

export const isNotificationForActiveConversation = (notification) => {
  const identity = notificationConversationIdentity(notification);
  if (!identity) return false;
  return Array.from(activeConversationSurfaces.values()).some((surface) => (
    surface.channel === identity.channel &&
    surface.conversationId === identity.conversationId
  ));
};

export const shouldSilenceConversationNotification = (notification) => {
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
    return false;
  }
  return isNotificationForActiveConversation(notification);
};
